import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { Printer } from 'lucide-react'
import { getJob } from '../../api/jobs'
import { getCustomers } from '../../api/master'
import { getCompany } from '../../api/settings'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay, localToday } from '../../utils/format'
import { canPrint, printPage, useAutoPrint } from '../../utils/print'

// A job's cutting list, for the workshop, laid out like the printed quotes
// and invoices (components/DocumentPrint.jsx, the docp styles): the company's
// logo and details, the job and its customer, then every person or garment
// the job is made to with its measurements, pieces, notes, and boxes to tick
// (cut, sewn, ready). The same sheet is shown on screen and printed on its
// own (body.ci-printing), with the job and page numbers at the foot.

const UNIT = { in: 'in', cm: 'cm' }
const cssString = (s) => `"${String(s || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, ' ')}"`
const initials = (name) => String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || 'AR'
const assetSrc = (path) => {
  const p = String(path || '').trim()
  if (!p) return ''
  if (/^(https?:)?\/\//i.test(p) || p.startsWith('data:')) return p
  return `${import.meta.env.VITE_API_URL || ''}${p}`
}
const preload = (src) => new Promise((resolve) => {
  if (!src || typeof Image === 'undefined') { resolve(); return }
  const img = new Image()
  img.onload = resolve
  img.onerror = resolve
  img.src = src
  setTimeout(resolve, 1200)
})

function Sheet({ company, job, customer, rows }) {
  const pieces = rows.reduce((n, r) => n + (Number(r.pieces) || 0), 0)
  const name = company?.company_name || ''
  const logo = assetSrc(company?.logo_path)
  const address = [company?.address].filter(Boolean)
  const contact = [company?.phone, company?.email, company?.website].filter(Boolean)
  const forLines = [
    customer?.address,
    [customer?.city, customer?.state, customer?.postal_code].filter(Boolean).join(', '),
    [customer?.phone, customer?.email].filter(Boolean).join(' · '),
  ].filter(Boolean)
  const units = [...new Set(rows.map((r) => (r.set.unit === 'cm' ? 'centimetres' : 'inches')))]

  return (
    <article className="docp cutp">
      <header className="docp-head">
        <div className="docp-brand">
          {logo ? <img className="docp-logo" src={logo} alt="" /> : <div className="docp-logo docp-logo-text">{initials(name)}</div>}
          <div>
            <div className="docp-company">{name}</div>
            {company?.tagline && <div className="docp-muted">{company.tagline}</div>}
            {address.map((l) => <div key={l} className="docp-muted">{l}</div>)}
            {contact.length > 0 && <div className="docp-muted">{contact.join(' · ')}</div>}
          </div>
        </div>
        <div className="docp-title">
          <h1>Cutting list</h1>
          <div className="docp-number">{job.job_number}</div>
        </div>
      </header>

      <section className="docp-parties">
        <div className="docp-billto">
          <div className="docp-label">For</div>
          <div className="docp-billto-name">{job.customer_name || '—'}</div>
          {forLines.map((l) => <div key={l} className="docp-muted">{l}</div>)}
        </div>
        <div className="docp-facts">
          <dl>
            <div><dt>Job</dt><dd>{job.name}</dd></div>
            <div><dt>Started</dt><dd>{job.start_date ? fmtDay(job.start_date) : '—'}</dd></div>
            <div><dt>Due</dt><dd>{job.end_date ? fmtDay(job.end_date) : '—'}</dd></div>
            <div><dt>Printed</dt><dd>{fmtDay(localToday())}</dd></div>
          </dl>
          <div className="docp-highlight">
            <span>Pieces to make</span>
            <strong>{pieces}</strong>
            <small>{rows.length} {rows.length === 1 ? 'set of measurements' : 'sets of measurements'}</small>
          </div>
        </div>
      </section>

      {rows.length === 0 ? (
        <p className="docp-muted">No measurements on this job yet. Add them on the job’s page.</p>
      ) : (
        <p className="cutp-note">Measurements in {units.join(' and ')}, as taken. Check them against the customer before cutting.</p>
      )}
      {rows.map((r, n) => {
        const values = (r.set.items || []).filter((i) => String(i.value || '').trim() !== '')
        const unit = UNIT[r.set.unit] || ''
        return (
          <section key={r.link_id} className="cutp-row">
            <div className="cutp-row-head">
              <span className="cutp-n">{n + 1}</span>
              <div className="cutp-row-main">
                <div className="cutp-row-title">{r.label || r.set.name}</div>
                <div className="docp-muted">
                  {[r.label && r.set.name, r.customer_name, r.set.unit === 'cm' ? 'centimetres' : 'inches', r.set.taken_on && `taken ${fmtDay(r.set.taken_on)}`].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="cutp-pieces"><span>Pieces</span><strong>{r.pieces}</strong></div>
            </div>
            {values.length > 0 ? (
              <dl className="cutp-grid">
                {values.map((i, k) => (
                  <div key={k}>
                    <dt>{i.label}</dt>
                    <dd>{i.value}{/^[\d.,\s/]+$/.test(i.value) ? ` ${unit}` : ''}</dd>
                  </div>
                ))}
              </dl>
            ) : <p className="docp-muted">No values entered.</p>}
            {r.set.notes && (
              <div className="cutp-notes"><span className="docp-label">Notes</span><p>{r.set.notes}</p></div>
            )}
            <div className="cutp-checks">
              <span><i aria-hidden="true" />Cut</span>
              <span><i aria-hidden="true" />Sewn</span>
              <span><i aria-hidden="true" />Ready</span>
              <span className="cutp-sign">Tailor</span>
            </div>
          </section>
        )
      })}
    </article>
  )
}

export default function CuttingList() {
  const { jobId } = useParams()
  const { notify } = useToast()
  const [job, setJob] = useState(null)
  const [company, setCompany] = useState(null)
  const [customer, setCustomer] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    getJob(jobId).then((j) => {
      setJob(j)
      if (j.customer_id) {
        getCustomers().then((rows) => {
          const list = Array.isArray(rows) ? rows : (rows?.rows || [])
          setCustomer(list.find((c) => String(c.customer_id) === String(j.customer_id)) || null)
        }).catch(() => {})
      }
    }).catch((e) => setError(e.message || 'Could not load the job.'))
    getCompany().then(setCompany).catch(() => setCompany({}))
  }, [jobId])

  const print = async () => {
    notify('Preparing the cutting list to print')
    await preload(assetSrc(company?.logo_path))
    const cleanup = () => document.body.classList.remove('ci-printing')
    document.body.classList.add('ci-printing')
    window.addEventListener('afterprint', cleanup, { once: true })
    setTimeout(() => { window.print(); setTimeout(cleanup, 1200) }, 50)
  }
  useAutoPrint(Boolean(job && company && (job.measurements || []).length), print)

  if (error) return <div className="ci-page"><ErrorBanner message={error} /></div>
  if (!job || !company) return <div className="ci-page"><Spinner label="Preparing the cutting list…" /></div>

  const rows = job.measurements || []
  const footer = `Cutting list ${job.job_number}${company?.company_name ? ` · ${company.company_name}` : ''}`

  return (
    <div className="ci-page cut-page">
      <div className="ci-header-row">
        <BackLink to={`/jobs/${job.job_id}`} label={job.name} />
        {canPrint() && <button type="button" className="ci-btn ci-btn-primary" onClick={() => printPage(print)} disabled={!rows.length}><Printer size={15} />Print</button>}
      </div>
      {!canPrint() && <p className="doc-hint">To print it, update the ASYS Retail app, or open this job on asysretail.com from a computer.</p>}

      <div className="cut-paper">
        <Sheet company={company} job={job} customer={customer} rows={rows} />
      </div>

      {createPortal(
        <div className="ci-print-wrap" aria-hidden="true">
          <style>{`@media print { @page { margin: 14mm 14mm 16mm; @bottom-left { content: ${cssString(footer)}; font: 8.5px Inter, system-ui, sans-serif; color: #64748b; } @bottom-right { content: "Page " counter(page) " of " counter(pages); font: 8.5px Inter, system-ui, sans-serif; color: #64748b; } } }`}</style>
          <Sheet company={company} job={job} customer={customer} rows={rows} />
        </div>,
        document.body,
      )}
    </div>
  )
}
